import { Fragment } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives';

import { LinkProps } from '@/libraries/design/components/link/src/link';
import { wacToken } from '@/libraries/design/foundations';

import { LinkColumn } from './layouts/subnav-link-columns';
import { DivOnHoverExit } from './nav-links-container';

export interface DropdownContentProps {
	contentSections: DropdownContentSectionProps[];
	orientation?: 'column' | 'row';
}

export const DropdownContent = (props: DropdownContentProps) => {
	const { contentSections } = props;
	const { orientation = 'row' } = props;

	return (
		<DivOnHoverExit>
			<div
				css={[
					orientation === 'row' && dropdownGridStyle,
					orientation === 'column' && dropdownColumnStyles,
				]}
			>
				{contentSections.map((section, idx) => (
					<Fragment key={`${section.header}-${idx}`}>
						<DropdownContentSection {...section} />
						{orientation === 'column' && idx < contentSections.length - 1 && (
							<div css={dropdownColumnDividerStyles} />
						)}
					</Fragment>
				))}
			</div>
		</DivOnHoverExit>
	);
};

interface DropdownContentSectionProps {
	header?: string;
	links: LinkProps[];
}

const DropdownContentSection = ({ header, links }: DropdownContentSectionProps) => {
	return (
		<LinkColumn>
			{header && (
				<h2 className="nav-column-header" css={headerStyle}>
					{header}
				</h2>
			)}
			<ul css={linkColumnStyle} role="menu">
				{links.map((link) => (
					<li key={link.text} role="none">
						<a
							role="menuitem"
							href={link.href}
							{...link.dataEventProps}
							data-event="clicked"
							data-event-component="linkButton"
							data-event-container="global-Product-Nav-link-in-dropdown"
							css={linkStyle}
							onClick={link.onClick}
							{...link.anchorProps}
						>
							{link.text}
						</a>
					</li>
				))}
			</ul>
		</LinkColumn>
	);
};

const dropdownGridStyle = css({
	padding: '20px',
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	gap: '32px',
	[media.above.md]: {
		padding: '40px',
		flexDirection: 'row',
	},
});

const dropdownColumnStyles = css({
	padding: '40px',
	paddingBlockStart: '20px',
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	gap: '16px',
});

const dropdownColumnDividerStyles = css({
	width: '100%',
	borderBlockStart: `solid 1px ${wacToken('color.neutral.dark1000')}`,
});

const headerStyle = css({
	textTransform: 'uppercase',
	color: wacToken('color.primary.black'),
	fontSize: '0.75rem',
	fontFamily: wacToken('font.charlie.text'),
	fontWeight: 700,
	lineHeight: '1rem',
});

const linkColumnStyle = css({
	display: 'grid',
	gridAutoRows: '40px',
	width: '184px',
	listStyle: 'none',
	margin: 0,
	padding: 0,
	marginBlockStart: '16px',
});

const linkStyle = css({
	margin: 0,
	color: '#101214',
	fontSize: '.875rem',
	alignSelf: 'end',
	':hover': {
		color: wacToken('color.extended.blue700'),
	},
});
